import { useMemo, useState } from "react"
import { MenuItem } from "@mui/material"
import { makeStyles } from "tss-react/mui"
import { MRT_ColumnDef } from "material-react-table"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { AccountingCustomReportsQuery, CustomReportsResponse, fetchCustomReports } from "../customReportsApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { MatchActualUserType } from "../../../../../accessControl/MatchActualUserType"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardRunCustomJobDialog } from "../components/StandardRunCustomJobDialog"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { UpdateCustomJobDialog } from "../components/UpdateCustomJobDialog"
import { MERCHANT_ACCOUNTING_ROUTE } from "../../../../../../constants/routes"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../../merchants/merchantHooks"
import { CustomJob, CustomReportsType } from "../customReportsTypes"
import { UserType } from "../../../../../login/loginTypes"

const useStyles = makeStyles()((theme) => ({
    activateText: {
        color: theme.palette.tailwind.green[600],
    },
    deactivateText: {
        color: theme.palette.tailwind.red[600],
    },
}))

enum DialogType {
    RUN = "run",
    UPDATE = "update",
}

const defaultConfirmDialogState = {
    showConfirmModal: false,
    item: null,
    type: DialogType.RUN,
}

const transformData = (data: CustomReportsResponse) => {
    return data.customJobs
}

export function MerchantCustomReports() {
    const { classes } = useStyles()
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()

    const [confirmModalState, setConfirmModalState] = useState<{
        showConfirmModal: boolean
        item: null | CustomJob
        type: DialogType
    }>(defaultConfirmDialogState)

    const customReports = useApiQuery(fetchCustomReports, {
        queryName: AccountingCustomReportsQuery.CUSTOM_REPORTS_LIST,
        dependencies: {
            merchantId,
            type: CustomReportsType.ACCOUNTING,
        },
        select: transformData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(customReports)

    const columns = useMemo<MRT_ColumnDef<CustomJob>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("merchant-accounting-custom-reports-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("merchant-accounting-custom-reports-table-interval-column"),
                accessorKey: "interval",
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("merchant-accounting-custom-reports-table-last-run-column"),
                    accessorKey: "lastRunTime",
                },
                (row) => row?.lastRunTime,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("merchant-accounting-custom-reports-table-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory],
    )

    return (
        <>
            <LocalizedStrict id="merchant-accounting-custom-reports-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_CUSTOM_REPORTS}
                    title="Custom reports"
                    icon={<FontAwesomeIcon icon={faShop} />}
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ACCOUNTING_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-accounting-custom-reports">
                            <Breadcrumb>Custom reports</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <ApiQueryTable
                columns={columns}
                initialState={{
                    density: "spacious",
                }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="merchant-accounting-custom-reports-table-button-details">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`details-${row.original.id}`}
                        >
                            Details
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="merchant-accounting-custom-reports-table-button-download-report">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                                setConfirmModalState({
                                    showConfirmModal: true,
                                    item: row.original,
                                    type: DialogType.RUN,
                                })
                            }}
                            key={`download-report-${row.original.id}`}
                        >
                            Download report
                        </MenuItem>
                    </LocalizedStrict>,
                    <MatchActualUserType userTypes={[UserType.SUPPORT]}>
                        <LocalizedStrict
                            id={
                                row.original.isActive
                                    ? "merchant-accounting-custom-reports-table-button-deactivate"
                                    : "merchant-accounting-custom-reports-table-button-activate"
                            }
                        >
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                    setConfirmModalState({
                                        showConfirmModal: true,
                                        item: row.original,
                                        type: DialogType.UPDATE,
                                    })
                                }}
                                key={`status-${row.original.id}`}
                                className={row.original.isActive ? classes.deactivateText : classes.activateText}
                            >
                                {row.original.isActive ? "Deactivate" : "Activate"}
                            </MenuItem>
                        </LocalizedStrict>
                    </MatchActualUserType>,
                ]}
            />
            {confirmModalState.showConfirmModal &&
                confirmModalState.item &&
                (confirmModalState.type === DialogType.RUN ? (
                    <StandardRunCustomJobDialog
                        item={confirmModalState.item}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultConfirmDialogState)}
                    />
                ) : (
                    <UpdateCustomJobDialog
                        item={confirmModalState.item}
                        open={confirmModalState.showConfirmModal}
                        onClose={() => setConfirmModalState(defaultConfirmDialogState)}
                    />
                ))}
        </>
    )
}
